/**
 * Typed CDX Pickups API surface. Thin wrappers over apiClient (apiJson for reads, apiPost for the
 * mutating routes so 409 / blocked bodies reach the UI). All routes live under /api/pickups/* and
 * carry the Entra bearer via X-Cdx-Bearer (see apiClient).
 */
import { apiJson, apiPost, type PostResult } from '@/lib/apiClient';
import type {
  MeResult,
  PickupsConfig,
  MemberLookupResult,
  EligibleListResult,
  PickupListResult,
  PickupRecord,
  PickupStatus,
  SmsResult,
} from '@/lib/pickups-types';

function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && String(v).length > 0) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function getMe(): Promise<MeResult> {
  return apiJson<MeResult>('/api/pickups/me');
}

export function getConfig(): Promise<PickupsConfig> {
  return apiJson<PickupsConfig>('/api/pickups/config');
}

/** Member lookup by id. Throws an Error with `.status===404` when the member is not in the roster. */
export function getMember(id: string): Promise<MemberLookupResult> {
  return apiJson<MemberLookupResult>(`/api/pickups/member${qs({ id })}`);
}

export function getEligible(opts: {
  limit?: number;
  cursor?: string | undefined;
  q?: string | undefined;
  kitStage?: string | undefined;
}): Promise<EligibleListResult> {
  return apiJson<EligibleListResult>(
    `/api/pickups/eligible${qs({ limit: opts.limit, cursor: opts.cursor, q: opts.q, kitStage: opts.kitStage })}`,
  );
}

export function getPickupList(opts: {
  status?: string | undefined;
  limit?: number;
  cursor?: string | undefined;
}): Promise<PickupListResult> {
  return apiJson<PickupListResult>(
    `/api/pickups/list${qs({ status: opts.status, limit: opts.limit, cursor: opts.cursor })}`,
  );
}

export function schedulePickup(body: {
  memberId: string;
  pickupDate?: string | undefined;
  notifySms?: boolean;
}): Promise<PostResult<PickupRecord>> {
  return apiPost<PickupRecord>('/api/pickups/schedule', body);
}

export function cancelPickup(body: {
  memberId: string;
  confirmationNumber: string;
}): Promise<PostResult<PickupRecord>> {
  return apiPost<PickupRecord>('/api/pickups/cancel', body);
}

export function sendSms(body: { memberId: string; kind?: string }): Promise<PostResult<SmsResult>> {
  return apiPost<SmsResult>('/api/pickups/sms', body);
}

/** A stable label for a pickup status, for pills/filters. */
export const PICKUP_STATUSES: PickupStatus[] = ['scheduled', 'canceled', 'failed', 'collected'];
